import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getClosestAirport } from 'selectors/flightsSelectors'
import type { RetailStoreType } from 'constants/retailStores'

/**
 * Hook to determine the user's preferred retail store based on their location.
 * Returns 'bondi' for NSW users and 'chadstone' for VIC users.
 * Returns undefined if the user is not in a retail store region.
 */
function usePreferredRetailStore(): RetailStoreType | undefined {
  const { geoStateName } = useContext(GeoContext)
  const postcode = useAppSelector(state => state.auth.account?.postcode)
  const closestAirport = useAppSelector(getClosestAirport)

  return useMemo(() => {
    // Check for VIC
    const isVic = geoStateName === 'VIC'
    const isVicPostcode = postcode?.startsWith('3')
    const isVicAirport = closestAirport?.code === 'MEL'

    if (isVic || isVicPostcode || isVicAirport) {
      return 'chadstone'
    }

    // Check for NSW
    const isNsw = geoStateName === 'NSW'
    const isNswPostcode = postcode?.startsWith('2')
    const isNswAirport = closestAirport?.code === 'SYD'

    if (isNsw || isNswPostcode || isNswAirport) {
      return 'bondi'
    }

    // User is not in a retail store region
    return undefined
  }, [geoStateName, postcode, closestAirport])
}

export default usePreferredRetailStore
